import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BadgeColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  className?: string;
}

// Predefined color palette for quick selection
const PRESET_COLORS = [
  '#f97316', // Orange (default)
  '#ef4444', // Red
  '#10b981', // Green
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#f59e0b', // Amber
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#ec4899', // Pink
  '#6366f1', // Indigo
  '#14b8a6', // Teal
  '#f43f5e', // Rose
  '#a855f7', // Violet
  '#22c55e', // Emerald
  '#eab308', // Yellow
  '#64748b', // Slate
];

export const BadgeColorPicker: React.FC<BadgeColorPickerProps> = ({
  value,
  onChange,
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);

  const handlePresetColorSelect = (color: string) => {
    onChange(color);
    setCustomColor(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (color: string) => {
    setCustomColor(color);
    onChange(color);
  };

  const isValidHexColor = (color: string) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  return (
    <div className={className}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full border border-border"
                style={{ backgroundColor: value }}
              />
              <span className="font-mono text-sm">{value}</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64" align="start">
          <div className="space-y-4">
            {/* Preset Colors */}
            <div>
              <Label className="text-sm font-medium">Preset Colors</Label>
              <div className="grid grid-cols-8 gap-2 mt-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => handlePresetColorSelect(color)}
                    className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                      value === color ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Custom Color Input */}
            <div>
              <Label className="text-sm font-medium">Custom Color</Label>
              <div className="flex gap-2 mt-2">
                <div className="relative flex-1">
                  <Input
                    type="text"
                    value={customColor}
                    onChange={(e) => handleCustomColorChange(e.target.value)}
                    placeholder="#ff0000"
                    className={`font-mono ${
                      !isValidHexColor(customColor) && customColor !== '' 
                        ? 'border-destructive' 
                        : ''
                    }`}
                  />
                  {!isValidHexColor(customColor) && customColor !== '' && (
                    <p className="text-xs text-destructive mt-1">
                      Invalid hex color format
                    </p>
                  )}
                </div>
                <input
                  type="color"
                  value={isValidHexColor(customColor) ? customColor : '#f97316'}
                  onChange={(e) => handleCustomColorChange(e.target.value)}
                  className="w-10 h-9 rounded border cursor-pointer"
                  title="Color picker"
                />
              </div>
            </div>

            {/* Color Preview */}
            <div>
              <Label className="text-sm font-medium">Preview</Label>
              <div className="mt-2 p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: value }}
                  />
                  <span className="text-sm">Badge Color Preview</span>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};